import type React from 'react';

import { Link, Stack, Typography } from '@mui/material';
import { Resources } from '@ragu-components/ord-resources';
import { canAccessWithPermissions } from '@ratto-components/ra-rbac';
import { usePermissions, useTranslate } from 'react-admin';

import { ResourceChipList } from 'src/components/resourceChipList';
import { openResourceInNewTab } from 'src/lib/openResourceInNewTab';

import { QueueChip } from './QueueChip';

import type { Agent } from '@ragu-components/ord-types';

export interface AgentQueuesListProps {
  filterField: 'user.id' | 'group.id';
  filterValue: string;
  excludeAgentId?: string;
  /** Already-translated heading shown above the list, e.g. to disambiguate what the list represents */
  title?: string;
}

export const AgentQueuesList = ({
  filterField,
  filterValue,
  excludeAgentId,
  title,
}: AgentQueuesListProps): React.ReactElement => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const canListQueues = canAccessWithPermissions({ permissions, action: 'list', resource: Resources.queues });

  const fiql =
    excludeAgentId !== undefined
      ? `${filterField}==${filterValue};id!=${excludeAgentId}`
      : `${filterField}==${filterValue}`;

  return (
    <Stack sx={{ flex: 1, width: '100%', minWidth: 0, minHeight: 0 }}>
      {title !== undefined && <Typography variant='subtitle2'>{title}</Typography>}
      {canListQueues && (
        <Typography variant='body2'>
          {translate('resources.agents.messages.edit_queues_description', { _: 'You can edit queues ' })}
          <Link
            component='button'
            type='button'
            onClick={() => {
              openResourceInNewTab(Resources.queues);
            }}
          >
            {translate('resources.agents.messages.edit_queues_link', { _: 'here' })}
          </Link>
        </Typography>
      )}
      <ResourceChipList resource={Resources.agents} filter={{ active: 'fiql', fiql }}>
        {(record) => <QueueChip record={record as Agent} disabled={false} />}
      </ResourceChipList>
    </Stack>
  );
};

export default AgentQueuesList;
